import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Select, Tabs, Breadcrumb, Button, Spin, Skeleton } from "antd";
import { DoubleLeftOutlined } from "@ant-design/icons";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import OverviewTabs from "./TabViewDetails/OverviewTab";
import TimelineTab from "./TabViewDetails/TimelineTab";
import MapViewTab from "./TabViewDetails/MapViewTab";
import { NotificationManager } from "react-notifications";
import overTimeScheduleService from "../services/OverTimeSchedule-tabs-service";
import { imgTopArrwo } from "../pages/Elite/imageAssets";
import { useDispatch, useSelector } from "react-redux";
import { setTableActionDetails } from "../actions/tableListing/tableListDataAction";
import { isShipmentTrackingEligible } from "../helper/shipmentTracking";
import { isO2WPath, isPublicO2WPath } from "../helper/commonData";

const { TabPane } = Tabs;
const { Option } = Select;

/** BL/container only from Timeline/GetCustomerMaterialAndPonumberAsync — no Redux fallback. */
const resolveShipmentTrackingIds = (dropdownData, selectedValue) => {
  const empty = { bL_Number: "", container_Number: "" };

  if (!Array.isArray(dropdownData) || dropdownData.length === 0) {
    return empty;
  }

  let selectedOption = null;
  if (selectedValue && selectedValue !== "all") {
    selectedOption = dropdownData.find((item) => item.value === selectedValue);
  } else {
    selectedOption = dropdownData[0];
  }

  if (!selectedOption) {
    return empty;
  }

  return {
    bL_Number: selectedOption.bL_Number || "",
    container_Number: selectedOption.container_Number || "",
  };
};

const TabViewDetails = ({ detailsTab, type, id }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const decodedId = id ? decodeURIComponent(id) : "";
  const tableDatalist = useSelector(
    (state) => state.TableListData.tableListData
  );
  const TableDetails = useSelector((state) => state.TableListData.tableDetails);
  const isO2WPage = isO2WPath();
  const isPublicView = isPublicO2WPath();
  const sapOrderFromQuery = searchParams.get("sapOrderNumber") || "";
  const labelIdFromQuery = searchParams.get("labelId") || "";
  const valueNameFromQuery = searchParams.get("valueName") || "";

  const resolvedLabelId =
    TableDetails.labelId || labelIdFromQuery || decodedId;
  const resolvedSapOrderNumber =
    TableDetails.SapOrderNumber || sapOrderFromQuery;
  const resolvedValueName =
    TableDetails.valueName || valueNameFromQuery || decodedId;

  const [activeTabs, setActiveTabs] = useState("");
  const [quantityCountsDetails, setQuantityCountsDetails] = useState({});
  const [isScrollVisible, setIsScrollVisible] = useState(false);
  const [poMaterilaDropdownData, setPoMaterilaDropdownData] = useState([]);
  const [selectedValue, setSelectedValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [distributionChannel, setDistributionChannel] = useState(null);
  const [actualDispatchDate, setActualDispatchDate] = useState(null);
  const [isDistributionChannelResolved, setIsDistributionChannelResolved] =
    useState(false);

  const isShipmentTrackingUrl = detailsTab === "shipmentTrackingDetails";
  const resolvedActualDispatchDate =
    TableDetails.dispatch_Actual || actualDispatchDate;
  const shipmentTrackingEligible = isShipmentTrackingEligible({
    distributionChannel,
    routeType: type,
    orderStatus: TableDetails.status,
    actualDispatchDate: resolvedActualDispatchDate,
  });
  const showShipmentTrackingTab =
    !isO2WPage && isDistributionChannelResolved && shipmentTrackingEligible;

  const shipmentTrackingIds = useMemo(
    () => resolveShipmentTrackingIds(poMaterilaDropdownData, selectedValue),
    [poMaterilaDropdownData, selectedValue]
  );

  useEffect(() => {
    if (!decodedId) return;
    if (isPublicView && labelIdFromQuery) return;

    const currentId = TableDetails.labelId || TableDetails.valueName;
    if (currentId === decodedId) return;

    dispatch(
      setTableActionDetails({
        labelId: decodedId,
        valueName: decodedId,
        SapOrderNumber: TableDetails.SapOrderNumber || sapOrderFromQuery || "",
        bL_Number: TableDetails.bL_Number || "",
        container_Number: TableDetails.container_Number || "",
        status: TableDetails.status || "",
        distribution_Channel: TableDetails.distribution_Channel || "",
        customer_PO_Date: TableDetails.customer_PO_Date || "",
        dispatch_Actual: TableDetails.dispatch_Actual || "",
      })
    );
  }, [
    decodedId,
    isPublicView,
    labelIdFromQuery,
    sapOrderFromQuery,
    TableDetails.labelId,
    TableDetails.valueName,
    TableDetails.SapOrderNumber,
    TableDetails.bL_Number,
    TableDetails.container_Number,
    TableDetails.status,
    TableDetails.distribution_Channel,
    TableDetails.customer_PO_Date,
    TableDetails.dispatch_Actual,
    dispatch,
  ]);

  useEffect(() => {
    if (detailsTab === "overviewDetails") {
      setActiveTabs("1");
    } else if (detailsTab === "timelineDetails") {
      setActiveTabs("3");
    } else if (isShipmentTrackingUrl && showShipmentTrackingTab) {
      setActiveTabs("2");
    }
  }, [detailsTab, isShipmentTrackingUrl, showShipmentTrackingTab]);

  useEffect(() => {
    if (
      isDistributionChannelResolved &&
      !isShipmentTrackingEligible({
        distributionChannel,
        routeType: type,
        orderStatus: TableDetails.status,
        actualDispatchDate: resolvedActualDispatchDate,
      }) &&
      isShipmentTrackingUrl
    ) {
      const encodedId = id ? `/${encodeURIComponent(id)}` : "";
      if (isPublicView) {
        navigate(
          `/public/o2w/overviewDetails/${type}${encodedId}${location.search}`
        );
      } else {
        const modulePath = isO2WPage ? "o2w" : "logistic";
        navigate(`/${modulePath}/overviewDetails/${type}${encodedId}`);
      }
      setActiveTabs("1");
    }
  }, [
    isDistributionChannelResolved,
    distributionChannel,
    resolvedActualDispatchDate,
    TableDetails.status,
    isShipmentTrackingUrl,
    id,
    type,
    isO2WPage,
    isPublicView,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!resolvedLabelId || isO2WPage) {
      setIsDistributionChannelResolved(true);
      return undefined;
    }

    let cancelled = false;

    const loadDistributionChannel = async () => {
      setIsDistributionChannelResolved(false);

      const inputData = {
        dropdownValue: selectedValue === "all" ? "" : selectedValue,
        labelValue: resolvedLabelId,
        flag: type,
        SapOrderNumber: resolvedSapOrderNumber,
      };

      try {
        const response =
          await overTimeScheduleService.overviewGetDayandTimelineChartDataAPI(
            inputData
          );

        if (cancelled) return;

        if (response.data.statusCode === 200) {
          const dayWiseData = response.data.responseData?.delayAnalyses;
          const timeLineData = response.data.responseData?.timelineCharts;
          const channel =
            dayWiseData?.[0]?.distribution_Channel ||
            timeLineData?.[0]?.distribution_Channel ||
            null;
          const resolvedDate =
            timeLineData?.[0]?.dispatch_Actual ||
            dayWiseData?.[0]?.dispatch_Actual ||
            null;
          setDistributionChannel(channel);
          setActualDispatchDate(resolvedDate);
        } else {
          setDistributionChannel(null);
          setActualDispatchDate(null);
        }
      } catch (error) {
        if (!cancelled) {
          setDistributionChannel(null);
          setActualDispatchDate(null);
          console.log(error, "Error loading distribution channel");
        }
      } finally {
        if (!cancelled) {
          setIsDistributionChannelResolved(true);
        }
      }
    };

    loadDistributionChannel();

    return () => {
      cancelled = true;
    };
  }, [
    resolvedLabelId,
    type,
    selectedValue,
    resolvedSapOrderNumber,
    isO2WPage,
  ]);

  useEffect(() => {
    if (!resolvedLabelId) return;
    getQuantityCountsData();
    getDropDownDataData();
  }, [resolvedLabelId, type, resolvedSapOrderNumber]);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsScrollVisible(true);
      } else {
        setIsScrollVisible(false);
      }
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  //// handle get Quantity Counter Data
  const getQuantityCountsData = async () => {
    setIsLoading(true);
    let inputData = {
      dropdownValue: selectedValue === "all" ? "" : selectedValue,
      labelValue: resolvedLabelId,
      flag: type,
      SapOrderNumber: resolvedSapOrderNumber,
    };
    await overTimeScheduleService
      .getQuantityCountsDataAPI(inputData)
      .then((response) => {
        if (response.data.statusCode === 200) {
          let finalData = response.data.responseData;
          if (finalData.length > 0) {
            setQuantityCountsDetails(finalData[0]);
          } else {
            setQuantityCountsDetails({});
          }
        } else {
          setQuantityCountsDetails({});
          NotificationManager.error(response.data.message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        console.log(error, "Error");
      });
  };
  /// handle get Drop down data
  const getDropDownDataData = async () => {
    setIsLoading(true);
    let inputData = {
      id:
        type === "material"
          ? tableDatalist.find((x) => x.material_id === resolvedLabelId)
              ?.material_id || resolvedLabelId
          : resolvedLabelId,
      dropdownflag: type,
      SapOrderNumber: resolvedSapOrderNumber,
    };
    await overTimeScheduleService
      .getMaterialDropDownDataAPI(inputData)
      .then((response) => {
        if (response.data.statusCode === 200) {
          let finalData = response.data.responseData;
          if (finalData.length > 0) {
            setPoMaterilaDropdownData(finalData);
            if (type !== "po" && type !== "material") {
              setSelectedValue(finalData[0].value);
            }
          } else {
            setSelectedValue(resolvedValueName);
          }
          setIsLoading(false);
        } else {
          setIsLoading(false);
          NotificationManager.error(response.data.message);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.log(error, "Error");
      });
  };
  /// handle tab onchange
  const handleTabChange = (key) => {
    setActiveTabs(key);
    const detailPath =
      key === "1"
        ? "overviewDetails"
        : key === "2"
          ? "shipmentTrackingDetails"
          : "timelineDetails";
    const encodedId = id ? `/${encodeURIComponent(id)}` : "";

    if (isPublicView) {
      navigate(
        `/public/o2w/${detailPath}/${type}${encodedId}${location.search}`
      );
      return;
    }

    const modulePath = isO2WPage ? "o2w" : "logistic";
    navigate(`/${modulePath}/${detailPath}/${type}${encodedId}`);
  };
  //// selection data code
  const handleSelectonChange = (value) => {
    setSelectedValue(value);
  };
  //// scroll to Top code
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  return (
    <Fragment>
      <div className="dashboard app-content content">
        <div className="content-wrapper">
          <div className="content-body">
            {!isPublicView && (
              <div className="details_view_title mb-15">
                <Breadcrumb className="details_breadcrum" separator=">">
                  <Breadcrumb.Item>
                    <Link
                      to={`${isO2WPage ? "/dashboard/" : "/logistic/"}${type}`}
                    >
                      Dashboard
                    </Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    <label>{type === "po" ? "PO" : type}</label>
                  </Breadcrumb.Item>
                </Breadcrumb>
                <Link to={`${isO2WPage ? "/dashboard/" : "/logistic/"}${type}`}>
                  <Button type="primary back_btn" icon={<DoubleLeftOutlined />}>
                    Back
                  </Button>
                </Link>
              </div>
            )}
            <Spin size="large" spinning={isLoading}>
              <div>
                <div className="col-md-12 custom-dropdown d-flex">
                  <label className="col-md-3 custom-label pr-10 mb-4 pt-1">
                    {type === "material" ? "Material " : "PO "}:
                    {resolvedValueName}
                  </label>
                  <div className="select-container col-md-9">
                    <Select
                      value={
                        selectedValue
                          ? selectedValue
                          : `Please Select ${type === "po" ? "Material" : "PO"}`
                      }
                      onChange={handleSelectonChange}
                      disabled={
                        poMaterilaDropdownData.length === 0 ? true : false
                      }
                    >
                      {type === "po" || type === "material" ? (
                        <Option value="all">All</Option>
                      ) : null}
                      {poMaterilaDropdownData &&
                        poMaterilaDropdownData?.map((option, index) => (
                          <Option key={index} value={option.value}>
                            {option.name}
                          </Option>
                        ))}
                    </Select>
                  </div>
                </div>
              </div>
              {isLoading ? (
                <Skeleton.Button
                  size={"large"}
                  shape={"large"}
                  block={true}
                  active={isLoading}
                />
              ) : (
                <div
                  className={
                    type === "po" || type === "material" ? "row" : "row mr_top"
                  }
                >
                  <div className="col-md-12 d-flex mb-4">
                    <div className="col-xs-4 pr-20">
                      <label className="material_label" title="Ordered Qty">
                        Ordered Qty:
                      </label>
                      <span className="bg-primary text-white p-1 rounded material_count">
                        {quantityCountsDetails.total_Ordered ||
                          quantityCountsDetails.sale_Unit
                          ? quantityCountsDetails.total_Ordered +
                          " " +
                          quantityCountsDetails.sale_Unit
                          : "00"}
                      </span>
                    </div>
                    <div className="col-xs-4 pr-20">
                      <label className="material_label" title="Delivered Qty">
                        Delivered Qty:
                      </label>
                      <span className="bg-success text-white p-1 rounded material_count">
                        {quantityCountsDetails.total_Completed ||
                          quantityCountsDetails.sale_Unit
                          ? quantityCountsDetails.total_Completed +
                          " " +
                          quantityCountsDetails.sale_Unit
                          : "00"}
                      </span>
                    </div>
                    <div className="col-xs-4 pr-20">
                      <label className="material_label" title="Pending Qty">
                        Pending Qty:
                      </label>
                      <span className="bg-dark text-white p-1 rounded material_count">
                        {quantityCountsDetails.total_Pending ||
                          quantityCountsDetails.sale_Unit
                          ? quantityCountsDetails.total_Pending +
                          " " +
                          quantityCountsDetails.sale_Unit
                          : "00"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Spin>
            <div className="row dashboard_tabs material_tabs">
              <Tabs
                activeKey={activeTabs}
                type="card"
                onChange={handleTabChange}
              >
                <TabPane tab="Overview Tab" key="1">
                  <OverviewTabs
                    name={resolvedValueName}
                    labelId={resolvedLabelId}
                    tabType={type}
                    dropdownValue={selectedValue}
                    SapOrderNumber={resolvedSapOrderNumber}
                  />
                </TabPane>
                {showShipmentTrackingTab && (
                  <TabPane tab="Shipment Tracking" key="2">
                    <MapViewTab
                      bL_Number={shipmentTrackingIds.bL_Number}
                      container_Number={shipmentTrackingIds.container_Number}
                    />
                  </TabPane>
                )}
                <TabPane tab="Timeline Tab" key="3">
                  <TimelineTab
                    labelId={resolvedLabelId}
                    tabType={type}
                    dropdownValue={selectedValue}
                    SapOrderNumber={resolvedSapOrderNumber}
                    distributionChannel={
                      TableDetails.distribution_Channel || distributionChannel || ""
                    }
                  />
                </TabPane>
                {/* <TabPane tab="Schedule Details" key="3"></TabPane> */}
              </Tabs>
            </div>
          </div>
          <div className="scroll-to-top">
            {isScrollVisible && (
              <div onClick={scrollToTop}>
                <img src={imgTopArrwo} alt="img" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default TabViewDetails;
